import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'

import { createPaginationWithCursorResultSchema } from '../database'

// Agreement template tags used in agreement content for signature/initial placeholders
export const AGREEMENT_TEMPLATE_TAGS = {
  SIGNATURE: '<SIGNATURE>',
  INITIAL: '<INITIAL>',
} as const

// Matches raw tags: <SIGNATURE>, <INITIAL>
export const AGREEMENT_TEMPLATE_TAGS_REGEX = new RegExp(
  `<(${Object.keys(AGREEMENT_TEMPLATE_TAGS).join('|')})>`,
  'gi'
)

// Matches HTML-encoded tags (from TinyMCE storage): &lt;SIGNATURE&gt;, &lt;INITIAL&gt;
// optionally wrapped in <span class="shortcode">...</span>
export const AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX = new RegExp(
  `(?:<span[^>]*class="shortcode"[^>]*>)?&lt;(${Object.keys(AGREEMENT_TEMPLATE_TAGS).join('|')})&gt;(?:</span>)?`,
  'gi'
)

// Signing-tag matching comes in two modes:
//
// DEFAULT (bare v2 tags only: <SIGNATURE>, <INITIAL>) — this MUST stay the
// default. The legacy mobile flow (servers/api agreements-profile) deliberately
// numbers shortcodes (<SIGNATURE> → <SIGNATURES1>) before storing so this count
// returns 0, which bypasses the "Expected N signatures" guard and enables
// one-at-a-time signature capture via APPEND mode. Counting numbered tags by
// default breaks native-app agreement creation and flips
// allSignaturesCollected in the append flow.
//
// LEGACY OPT-IN (`includeLegacyTags: true`) — additionally matches the v1
// (onefitstop) vocabulary carried by migrated/prod-copied templates, where the
// count must equal the number of pads the public signing page renders:
//   - numbered multi-sig tags: <SIGNATURES1>, <INITIAL3>
//   - the v1 combined field: <SIGNATUREDATEANDCLIENTNAME>
// Signature-type tokens start with SIGNATURE; initial-type with INITIAL.
// Only the public signing flow (client-public-signing.service.ts) opts in.
const SIGNING_TAG_RAW_REGEX = /<(SIGNATURE|INITIAL)>/gi
const SIGNING_TAG_ENCODED_REGEX =
  /(?:<span[^>]*class="shortcode"[^>]*>)?&lt;(SIGNATURE|INITIAL)&gt;(?:<\/span>)?/gi
const LEGACY_SIGNING_TAG_RAW_REGEX = /<(SIGNATURE[A-Z]*[0-9]*|INITIAL[A-Z]*[0-9]*)>/gi
const LEGACY_SIGNING_TAG_ENCODED_REGEX =
  /(?:<span[^>]*class="shortcode"[^>]*>)?&lt;(SIGNATURE[A-Z]*[0-9]*|INITIAL[A-Z]*[0-9]*)&gt;(?:<\/span>)?/gi

// Count signature/initial tags in content, handling raw and HTML-encoded forms.
// Pass `includeLegacyTags: true` to also count numbered / variant v1 tags —
// see the mode notes above before changing the default.
export const countAgreementTags = (
  content: string,
  options?: { includeLegacyTags?: boolean }
): { signatureCount: number; initialCount: number; total: number } => {
  let signatureCount = 0
  let initialCount = 0
  const rawSource = options?.includeLegacyTags
    ? LEGACY_SIGNING_TAG_RAW_REGEX
    : SIGNING_TAG_RAW_REGEX
  const encodedSource = options?.includeLegacyTags
    ? LEGACY_SIGNING_TAG_ENCODED_REGEX
    : SIGNING_TAG_ENCODED_REGEX

  // Try raw tags first
  const rawRegex = new RegExp(rawSource.source, rawSource.flags)
  let match
  while ((match = rawRegex.exec(content)) !== null) {
    if (/^SIGNATURE/i.test(match[1]!)) {
      signatureCount++
    } else {
      initialCount++
    }
  }

  // If no raw tags found, try encoded tags
  if (signatureCount === 0 && initialCount === 0) {
    const encodedRegex = new RegExp(encodedSource.source, encodedSource.flags)
    while ((match = encodedRegex.exec(content)) !== null) {
      if (/^SIGNATURE/i.test(match[1]!)) {
        signatureCount++
      } else {
        initialCount++
      }
    }
  }

  return { signatureCount, initialCount, total: signatureCount + initialCount }
}

// Counts the signature/initial PADS a member actually signs, matching the pad
// derivation the native app consumes (servers/api policy.service.legacy.ts
// numbers unnumbered shortcodes to <SIGNATURES1>/<INITIAL1> and renders one pad
// per <SIGNATURES\d+> / <INITIAL\d+>). Unlike countAgreementTags' two modes,
// this recognises BOTH unnumbered (<SIGNATURE>, <SIGNATURES>, <INITIAL>) and
// numbered (<SIGNATURES1>, <INITIAL2>) pads as one pad each, while never
// counting v1 data shortcodes such as <SIGNATUREDATEANDCLIENTNAME> (letters
// after SIGNATURE mark an auto-filled data field, not a pad). Used by the
// one-at-a-time (append-mode) signing flow to know when every pad has been
// collected so the agreement can flip PENDING → ACTIVE (MEM-51/CORE-1362).
// Exported so every signature renderer (the server signed-PDF sanitiser and the
// Core client-side download generator) embeds images against the SAME pad
// definition countSignaturePads uses — a drift here is what left multi-sig /
// numbered-tag templates rendering all signatures at the end (MEM-51).
export const SIGNATURE_PAD_RAW_REGEX = /<(SIGNATURES?\d*|INITIAL\d*)>/gi
export const SIGNATURE_PAD_ENCODED_REGEX =
  /(?:<span[^>]*class="shortcode"[^>]*>)?&lt;(SIGNATURES?\d*|INITIAL\d*)&gt;(?:<\/span>)?/gi

export const countSignaturePads = (content: string): number => {
  let count = 0

  const rawRegex = new RegExp(SIGNATURE_PAD_RAW_REGEX.source, SIGNATURE_PAD_RAW_REGEX.flags)
  while (rawRegex.exec(content) !== null) {
    count++
  }

  if (count === 0) {
    const encodedRegex = new RegExp(
      SIGNATURE_PAD_ENCODED_REGEX.source,
      SIGNATURE_PAD_ENCODED_REGEX.flags
    )
    while (encodedRegex.exec(content) !== null) {
      count++
    }
  }

  return count
}

// Agreement enums
export const agreementTypeEnum = z
  .enum(['SERVICE_AGREEMENT', 'POLICY', 'WAIVER'])
  .register(asEnumType, {
    name: 'AgreementType',
    description: 'Type of agreement: service agreement, policy, or waiver',
  })

/**
 * The `agreementMethod` values written by the platform migration, mirroring
 * `mapAgreementMethod` in the hapana-dataform migration mapping. None of them is in
 * `agreementMethodEnum` — the app never writes these — and `agreementMethod` is an
 * unconstrained STRING(20), so this list is the only thing that distinguishes a
 * migrated row from one the app created.
 *
 * It decides visibility in the client Documents tab: a migrated row is listed, a
 * member's own unpaid one is withheld. If a later migration pass introduces a fifth
 * value, add it here or those rows go dark with nothing failing.
 */
export const MIGRATED_AGREEMENT_METHODS = ['WEB', 'APP', 'KIOSK', 'MANUAL'] as const

export const agreementStatusEnum = z
  .enum(['PENDING', 'ACTIVE', 'EXPIRED', 'REVOKED', 'COMPLETED'])
  .register(asEnumType, {
    name: 'AgreementStatus',
    description: 'Status of client agreement',
  })

export const agreementMethodEnum = z
  .enum(['SIGNATURE', 'CHECKBOX', 'ELECTRONIC', 'TERMINAL', 'PAPER', 'IMPORTED'])
  .register(asEnumType, {
    name: 'AgreementMethod',
    description: 'Method used to accept agreement',
  })

// Surface that captured the signature for a pending-checkout agreement.
// Drives the audit-event source and the certificate token-ID prefix when the
// deferred flatten runs at activation.
export const agreementSignedSourceEnum = z
  .enum(['EMBED_WEB', 'MOBILE', 'KIOSK', 'TERMINAL'])
  .register(asEnumType, {
    name: 'AgreementSignedSource',
    description: 'Client surface that captured the agreement signature',
  })

// Client Agreement Schema
export const clientAgreementSchema = z
  .object({
    agreementId: z.uuid(),
    clientId: z.uuid(),
    siteId: z.uuid(),
    siteAgreementTemplateId: z.uuid().nullable(),
    serviceId: z.uuid().nullable(),
    type: agreementTypeEnum,
    agreementVersion: z.string(),
    agreementContent: z.string(),
    agreementName: z.string(),
    signatureImageUrls: z.array(z.string()).nullable(),
    agreementMethod: agreementMethodEnum.nullable(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    signedAt: z.coerce.date().nullable(),
    expiresAt: z.coerce.date().nullable(),
    status: agreementStatusEnum,
    createdAt: z.coerce.date(),
    publicAccessLinkId: z.uuid().nullish(),
    signedViewLinkId: z.uuid().nullish(),
    signedPdfGcsUri: z.string().nullish(),
    signedPdfSha256: z.string().nullish(),
    sentByStaffUserId: z.uuid().nullish(),
    sentAt: z.coerce.date().nullish(),
    voidedAt: z.coerce.date().nullish(),
    voidedByStaffUserId: z.uuid().nullish(),
    voidReason: z.string().nullish(),
    linkedInvoiceId: z.uuid().nullish(),
    signatureOnly: z.boolean().nullish(),
    terminalHandoffRequestId: z.uuid().nullish(),
    nativeSignatureUrls: z.array(z.string()).nullish(),
  })
  .register(asObjectType, {
    name: 'ClientAgreement',
  })

// Site Agreement Template Schema
export const siteAgreementTemplateSchema = z
  .object({
    siteId: z.uuid(),
    siteAgreementTemplateId: z.uuid(),
    type: agreementTypeEnum,
    name: z.string(),
    version: z.string(),
    content: z.string(),
    templateUrl: z.string().nullable(),
    requiresSignature: z.boolean(),
    validityDays: z.number().nullable(),
    isActive: z.boolean(),
    effectiveDate: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .register(asObjectType, {
    name: 'SiteAgreementTemplate',
  })

// Create template input — picks fields from the template schema and relaxes optionality
export const createSiteAgreementTemplateInputSchema = siteAgreementTemplateSchema
  .pick({
    siteId: true,
    type: true,
    name: true,
    content: true,
    templateUrl: true,
  })
  .extend({
    name: z.string().min(1).max(200),
    version: z.string().min(1).max(20),
    templateUrl: z.string().max(500),
    requiresSignature: z.boolean().optional(),
    validityDays: z.number().int().optional(),
    effectiveDate: z.coerce.date().optional(),
  })
  .register(asObjectType, {
    name: 'CreateSiteAgreementTemplateInput',
  })

// Sign agreement input — picks identity fields from agreement schema and adds signing fields
export const signClientAgreementInputSchema = clientAgreementSchema
  .pick({
    agreementId: true,
    clientId: true,
    siteId: true,
  })
  .extend({
    signatures: z.array(z.string()).min(0),
    agreementMethod: agreementMethodEnum,
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'SignClientAgreementInput',
    description:
      'Input for signing a client agreement with base64 signature images (one per <SIGNATURE> or <INITIAL> tag)',
  })

// Create and sign agreement in one step — for inline agreement text (no template)
export const createAndSignAgreementInputSchema = z
  .object({
    clientId: z.uuid(),
    siteId: z.uuid(),
    agreementName: z.string().min(1).max(200),
    agreementContent: z.string().min(1),
    signatures: z.array(z.string()).min(0),
    agreementMethod: agreementMethodEnum,
    // `.nullish()` (nullable + optional), matching the sibling
    // appendSignatureToAgreement args: the legacy bridge passes an explicit
    // `null` for these when it can't resolve them (e.g. no client IP), which a
    // bare `.optional()` would reject at runtime. `.max(500)` on userAgent
    // matches the `ClientAgreement.userAgent STRING(500)` column so an
    // oversized UA can't fail the Spanner write on this evidence path.
    ipAddress: z.string().max(45).nullish(),
    userAgent: z.string().max(500).nullish(),
    agreementVersion: z.string().min(1).max(20).nullish(),
    siteAgreementTemplateId: z.uuid().nullish(),
    pendingCheckout: z.boolean().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'CreateAndSignAgreementInput',
    description:
      'Input for creating and signing an agreement in one step from inline agreement text. ' +
      'Set pendingCheckout=true to keep status PENDING until checkout completes (prevents premature visibility).',
  })

// Response schema for sending agreement email
export const sendAgreementResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    // The freshly minted AGREEMENT_SIGN URL. Returned so staff can copy the
    // link directly (skipEmail sends); additive — older consumers ignore it.
    publicUrl: z.string().nullish(),
  })
  .register(asObjectType, {
    name: 'SendAgreementResponse',
    description: 'Response for sending agreement notification email',
  })

// Pagination schemas
export const clientAgreementsFiltersSchema = z
  .object({
    status: z
      .object({
        value: agreementStatusEnum,
      })
      .optional(),
  })
  .optional()

export const clientAgreementsPaginationArgsSchema = z.object({
  clientId: z.string(),
  siteId: z.string().optional(),
  filters: clientAgreementsFiltersSchema,
  pageSize: z.number().optional(),
  cursor: z.string().optional(),
  search: z.string().optional(),
  /**
   * MEM-524: return the member's own in-progress purchases too — the rows the
   * Documents view withholds until a sale lands.
   *
   * Only a caller that needs the held rows themselves should set this. The
   * compatibility REST API in `servers/api/src/modules/legacy/` does: the mobile
   * app's signature-reuse lookup exists to find a member's held agreement and hand
   * their signatures back, so hiding those would make multi-signature members
   * re-sign every pad. A status filter alone is NOT that signal — the Core
   * Documents tab offers a PENDING filter, and treating it as one let one click
   * list every withheld row.
   */
  includeHeldAgreements: z.boolean().optional(),
})

export const clientAgreementsPaginationResultSchema =
  createPaginationWithCursorResultSchema(clientAgreementSchema)

// Type exports
export type ClientAgreementSchema = z.infer<typeof clientAgreementSchema>
export type AgreementTypeSchema = z.infer<typeof agreementTypeEnum>
export type AgreementStatusSchema = z.infer<typeof agreementStatusEnum>
export type AgreementMethodSchema = z.infer<typeof agreementMethodEnum>
export type SiteAgreementTemplateSchema = z.infer<typeof siteAgreementTemplateSchema>
export type CreateSiteAgreementTemplateInputSchema = z.infer<
  typeof createSiteAgreementTemplateInputSchema
>
export type SignClientAgreementInputSchema = z.infer<typeof signClientAgreementInputSchema>
export type CreateAndSignAgreementInputSchema = z.infer<typeof createAndSignAgreementInputSchema>
export type AgreementSignedSourceSchema = z.infer<typeof agreementSignedSourceEnum>
export type SendAgreementResponseSchema = z.infer<typeof sendAgreementResponseSchema>
